import { atom } from 'jotai';
import { mutationAtomWithLoadable } from 'lib/atoms';
import request from 'lib/request';
import { acreageBasisRefreshAtom, refetchNriDataAtom } from '../atoms';
import type { AcreageBasis } from '../types';

/**
 * Unsaved factor drafts, keyed per UAT (`subtractId:uatGisUuid`). BaseCard
 * closes its popup on ANY outside click, unconditionally — so a typed-but-
 * unsaved factor would otherwise be lost silently. The panel stashes the
 * draft here when it unmounts dirty and re-seeds from it on reopen, making
 * the loss recoverable without blocking dismissal (#7418 review). Escape
 * (an explicit discard) and a successful save both clear the entry.
 */
export const acreageDraftStashAtom = atom<Record<string, number | ''>>({});

/**
 * Fallbacks only. The backend owns this rule — it decides when a stored override
 * is discarded as drifted — and now serves both values on the acreage block, so
 * callers pass what the server said. These stand in for a payload that predates
 * the served fields; a hand-maintained mirror desyncs silently, because nothing
 * fails when one side moves.
 */
export const OVERLAP_TOLERANCE = 0.1;
export const DRIFT_PRECISION = 6;

interface SaveAcreageBasisArgs {
  subtractId: string;
  /** The UAT's gis_uuid — stable across the half-hourly UAT reimport. */
  uatGisUuid: string;
  acreageBasis: AcreageBasis;
  /** null follows the shapes; a value overrides them. */
  overlapFraction?: number | null;
}

export interface AcreageBasisResponse {
  id: number;
  subtractId: string;
  uatGisUuid: string;
  acreageBasis: AcreageBasis;
  overlapFraction: number | null;
  /**
   * The recalculation this save triggered recorded errors. The choice itself is
   * saved regardless — it commits before the recalculation runs — so this says
   * the figures are stale, not that the save failed.
   */
  recalculationFailed?: boolean;
  /**
   * The rewrite was queued rather than run in the request, because this subtract
   * has more calculations than one request can safely rewrite. The choice is
   * saved; the figures beside it are still the previous ones.
   */
  recalculationDeferred?: boolean;
}

/** The shapes `request.put` resolves to, narrowed to what needs reporting. */
export type SaveResponse =
  | { type: 'success'; data?: AcreageBasisResponse }
  | { type: 'error'; data?: { errors?: Record<string, string[]> } }
  | { type: 'fatal' | 'abort'; message?: string };

/**
 * Turns a rejected save into something worth reading. The server answers a bad
 * overlap with field errors; everything else (a permission error, an expired
 * CSRF token, a dropped connection) arrives as fatal with a message.
 *
 * Shared by the dropdown and the overlap editor: both write through the same
 * endpoint, and a save that fails silently in either one is indistinguishable
 * from the control refusing to change.
 *
 * @param {SaveResponse | undefined} response - What the mutation resolved to
 * @param {string} fallback - Message when the server offered no reason
 * @return {string} A message to show the user
 */
export const describeSaveFailure = (
  response: SaveResponse | undefined,
  fallback: string
): string => {
  if (response && response.type === 'error') {
    const errors = response.data?.errors ?? {};
    const messages = Object.entries(errors).map(
      ([field, list]) => `${field.replace(/_/g, ' ')} ${list.join(', ')}`
    );
    if (messages.length) return messages.join('; ');
  }
  if (response && 'message' in response && response.message) {
    return response.message;
  }

  return fallback;
};

/**
 * Whether the save landed but the NRI rewrite it triggers did not.
 *
 * The choice is committed before the recalculation runs, so this is not a failed
 * save — the dropdown is right to show the new basis. What is wrong is every
 * figure derived from it, with nothing on screen to say so.
 *
 * @param {SaveResponse | undefined} response - What the mutation resolved to
 * @return {boolean} True when the server reported a failed recalculation
 */
export const recalculationFailed = (
  response: SaveResponse | undefined
): boolean =>
  response?.type === 'success' && response.data?.recalculationFailed === true;

/**
 * Whether the save landed but the rewrite was handed to the queue.
 *
 * Distinct from a failure: nothing has gone wrong, the figures are simply not
 * updated yet and nothing will announce when they are. Only subtracts above the
 * inline limit take this path — 0.087% of them in production — and the
 * alternative is a request that dies partway through destroying and rebuilding
 * their calculations.
 *
 * @param {SaveResponse | undefined} response - What the mutation resolved to
 * @return {boolean} True when the server queued the rewrite
 */
export const recalculationDeferred = (
  response: SaveResponse | undefined
): boolean =>
  response?.type === 'success' && response.data?.recalculationDeferred === true;

/**
 * Persists a UAT's acreage basis.
 *
 * The server recalculates NRI inside the same request for all but the largest
 * subtracts, so by the time this resolves the new interest values are usually
 * already stored — which is why a successful save refetches them rather than
 * waiting for a notification. Above the inline limit the rewrite is queued and
 * the response says so; nothing is pushed when either one finishes, so the
 * caller reports the deferral rather than the page waiting on an event that
 * never arrives.
 *
 * The stored basis is read off the current_development payload, so a successful
 * save refetches that too: the dropdown is fully controlled by it and would
 * otherwise snap back to the pre-save value.
 */
export const [saveAcreageBasisAtom, saveAcreageBasisLoadableAtom] =
  mutationAtomWithLoadable(async (_get, set, args: SaveAcreageBasisArgs) => {
    const response = await request.put<
      AcreageBasisResponse,
      { errors: Record<string, string[]> }
    >(
      `/crm/subtracts/${args.subtractId}/uat_acreage_parameters/${args.uatGisUuid}`,
      {
        uat_acreage_parameter: {
          acreage_basis: args.acreageBasis,
          overlap_fraction: args.overlapFraction ?? null,
        },
      },
      new AbortController()
    );

    if (response.type === 'success') {
      set(acreageBasisRefreshAtom, (n) => n + 1);
      // The interest values on the cards come from slot_allocations, a
      // separate payload from the one above, and the worked equation takes its
      // result from there too. Without this the operands update and the result
      // does not, so the popup disagrees with itself. The participation tract
      // factor control already refreshes it the same way.
      set(refetchNriDataAtom);
    }

    return response;
  });

/**
 * Whether the stamped calculation still describes the previous choice.
 *
 * The recalculation runs inside the save request, so this is normally false by
 * the time anything reads it — it catches the window before a refetched payload
 * arrives, and any row stamped by the earlier queued design.
 *
 * It is deliberately not the whole story: a row whose legal basis could not be
 * applied disagrees with its stamp permanently, and reporting that as pending
 * promises an update that never comes. Callers must check the stamped warnings
 * before presenting this as a transient state — see UatAcreageBasisPanel.
 *
 * @param {object} args - The saved choice and what the stamp reports
 * @param {string} args.chosenBasis - Basis on the stored parameter row
 * @param {string | undefined} args.stampedBasis - Basis the calculation recorded
 * @param {number | null | undefined} args.storedOverlapFraction - Saved override
 * @param {number | null | undefined} args.stampedOverlapFraction - Stamped factor
 * @return {boolean} True while the stamp is behind the saved choice
 */
export const stampIsPending = ({
  chosenBasis,
  stampedBasis,
  storedOverlapFraction,
  stampedOverlapFraction,
}: {
  chosenBasis: AcreageBasis;
  stampedBasis: AcreageBasis | undefined;
  storedOverlapFraction: number | null | undefined;
  stampedOverlapFraction: number | null | undefined;
}): boolean => {
  if ((stampedBasis ?? 'gis') !== chosenBasis) return true;

  // No override means the stamp is free to hold whatever the shapes measured,
  // so only a saved one can be contradicted.
  if (storedOverlapFraction === null || storedOverlapFraction === undefined) {
    return false;
  }
  if (stampedOverlapFraction === null || stampedOverlapFraction === undefined) {
    return true;
  }

  return Math.abs(stampedOverlapFraction - storedOverlapFraction) > 1e-9;
};

/**
 * How far a manually entered overlap sits from what the shapes measure. Used to
 * warn without blocking — the land team is allowed to disagree with the map, but
 * a large gap usually means the wrong tool is being reached for.
 *
 * @param {number | null | undefined} entered - The user's overlap fraction
 * @param {number | null | undefined} asMapped - The GIS-measured overlap fraction
 * @param {number} tolerance - Deviation allowed before warning; served by the backend
 * @param {number} precision - Decimal places the ratio is rounded to before comparing
 * @return {boolean} True when the entry deviates beyond the tolerance
 */
export const deviatesBeyondTolerance = (
  entered: number | null | undefined,
  asMapped: number | null | undefined,
  tolerance: number = OVERLAP_TOLERANCE,
  precision: number = DRIFT_PRECISION
): boolean => {
  if (
    entered === null ||
    entered === undefined ||
    asMapped === null ||
    asMapped === undefined ||
    asMapped === 0
  ) {
    return false;
  }

  // Rounded before comparing, exactly as the backend does. In float arithmetic
  // (0.55 - 0.50) / 0.50 is 0.10000000000000009, so without this an entry sitting
  // exactly on the tolerance warns here while the server — which owns the rule,
  // because it enforces the drift reset — does not.
  return (
    Number((Math.abs(entered - asMapped) / asMapped).toFixed(precision)) >
    tolerance
  );
};
